import { supabase } from '../lib/supabaseClient';

export const usersService = {
  // Enhanced session validation helper
  async _validateSession() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      if (error || !session) {
        console.error('Session validation failed:', error);
        return { valid: false, error: 'Authentication session missing. Please log in again.' };
      }
      return { valid: true, session };
    } catch (error) {
      console.error('Session validation error:', error);
      return { valid: false, error: 'Failed to validate authentication session.' };
    }
  },

  // Get all users - alias for getActiveUsers for backward compatibility
  async getUsers(options = {}) {
    return this.getActiveUsers(options);
  },

  // Get all active user profiles for dropdowns and filters
  async getActiveUsers(options = {}) {
    try {
      // Validate session before making API calls
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const currentUserId = sessionCheck?.session?.user?.id;
      if (!currentUserId) {
        return { success: false, error: 'Unable to determine current user' };
      }

      let resolvedTenantId = options?.tenantId || null;

      if (!resolvedTenantId) {
        const { data: profileValidation, error: validationError } = await supabase
          ?.rpc('validate_user_session_and_profile', { user_uuid: currentUserId });

        if (validationError) {
          console.error('Get users - tenant validation error:', validationError);
          return { success: false, error: 'Failed to validate user tenant context' };
        }

        if (!profileValidation?.success || !profileValidation?.user_data?.tenant_id) {
          return { success: false, error: 'Unable to determine tenant context for current user' };
        }

        resolvedTenantId = profileValidation?.user_data?.tenant_id;
      }

      let query = supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role, tenant_id');

      if (!options?.includeInactive) {
        query = query?.eq('is_active', true);
      }

      if (resolvedTenantId && resolvedTenantId !== 'all-tenants') {
        query = query?.eq('tenant_id', resolvedTenantId);
      }

      const { data, error } = await query?.order('full_name', { ascending: true });

      if (error) {
        console.error('Get users error:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data: (data || [])?.map(user => ({
        ...user,
        tenant_id: user?.tenant_id
      })) };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: 'Failed to load users' };
    }
  },

  // Get users by role for specific filtering
  async getUsersByRole(role) {
    try {
      // Validate session before making API calls
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role')
        ?.eq('role', role)
        ?.eq('is_active', true)
        ?.order('full_name', { ascending: true });

      if (error) {
        console.error('Get users by role error:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: 'Failed to load users by role' };
    }
  },

  // Get a specific user profile
  async getUser(userId) {
    if (!userId) return { success: false, error: 'User ID is required' };

    try {
      // Validate session before making API calls
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role, phone, is_active')
        ?.eq('id', userId)
        ?.single();

      if (error) {
        console.error('Get user error:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: 'Failed to load user' };
    }
  },

  // Create new user (Super Admin function)
  async createUser(userData) {
    try {
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const { data: result, error } = await supabase?.rpc(
        'create_admin_user_with_workflow',
        {
          user_email: userData?.email,
          user_full_name: userData?.fullName,
          user_role: userData?.role || 'rep',
          user_organization: userData?.organization || null,
          user_tenant_id: userData?.tenantId || null,
          temporary_password: userData?.tempPassword || null,
          send_confirmation_email: userData?.sendEmail !== false
        }
      );

      if (error) {
        return { success: false, error: error?.message };
      }

      const userResult = result?.[0];
      return {
        success: userResult?.success || false,
        data: userResult,
        error: userResult?.success ? null : userResult?.message
      };
    } catch (error) {
      console.error('Create user error:', error);
      return { success: false, error: 'Failed to create user' };
    }
  },

  // Resend confirmation email
  async resendConfirmation(userId) {
    try {
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const { data: result, error } = await supabase?.rpc(
        'resend_confirmation_workflow',
        { user_uuid: userId }
      );

      if (error) {
        return { success: false, error: error?.message };
      }

      const resendResult = result?.[0];
      return {
        success: resendResult?.success || false,
        data: resendResult,
        error: resendResult?.success ? null : resendResult?.message
      };
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { success: false, error: 'Failed to resend confirmation email' };
    }
  },

  // Reset user password
  async resetUserPassword(userId) {
    try {
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      const { data: result, error } = await supabase?.rpc(
        'admin_force_password_reset',
        { target_user_id: userId }
      );

      if (error) {
        return { success: false, error: error?.message };
      }

      const resetResult = result?.[0];
      return {
        success: resetResult?.success || false,
        data: resetResult,
        error: resetResult?.success ? null : resetResult?.message
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return { success: false, error: 'Failed to reset password' };
    }
  },

  // Add function to find user by email across multiple sources
  async findUserByEmail(email) {
    if (!email) return { success: false, error: 'Email is required' };

    try {
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      // Search user_profiles table
      const { data: profiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('email', email);

      if (profileError && profileError?.code !== 'PGRST116') {
        console.error('Error searching user profiles:', profileError);
      }

      return { 
        success: true, 
        data: {
          found: profiles && profiles?.length > 0,
          profiles: profiles || [],
          email: email
        }
      };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: 'Failed to search for user' };
    }
  },

  // Add function to resync user with proper tenant assignment
  async resyncUserTenant(userId, tenantId) {
    if (!userId) return { success: false, error: 'User ID is required' };

    try {
      const sessionCheck = await this._validateSession();
      if (!sessionCheck?.valid) {
        return { success: false, error: sessionCheck?.error };
      }

      // Update user profile with tenant assignment
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ 
          tenant_id: tenantId,
          is_active: true,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) {
        console.error('Resync user tenant error:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: 'Failed to resync user tenant' };
    }
  }
};
